import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { approvalMyWOFormModel } from '../../../form-model/approval-wo.form.model';
import { Cluster, Loc, Queue, RepairMode } from '../../../models/loc.model';
import { firstValueFrom, takeUntil, catchError, ReplaySubject, tap, switchMap, of, from } from 'rxjs';
import { ApprovalWoService } from '../../../services/approval-wo.service';
import { ToastrService } from '../../../services/toastr-service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { Shop } from '../../../models/shop.model';
import { MasterDataService } from '../../../services/master-data.service';
import { assignWOFilter, searchWOList } from '../../../models/manual-approval-filter.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ApprovalMasterData } from '../../../models/approval-wo.model';
import { ManualApprovalHeader } from '../../../models/manual-approval-header.model';
import { ApprovalMyWoGridComponent } from '../approval-my-wo-grid/approval-my-wo-grid.component';
import { KeyValue } from '../../../models/loc.model';
import { MyWoStateService } from '../my-wo-state.service';
import { AuthHelper } from '../../../auth/auth-helper';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
@Component({
  selector: 'app-filter-dropdowns',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, ApprovalMyWoGridComponent, AemsMultilineTagInputComponent],
  templateUrl: './filter-dropdowns.component.html',
  styleUrl: './filter-dropdowns.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FilterDropdownsComponent {
  disableAssignBtn: boolean = false;
  isUnassigning: boolean = false;
  queueList: Queue[] = [];
  locList: Loc[] = [];
  clusterList: Cluster[] = [];
  countrycluster: Cluster[] = [];
  region: Loc[] = [];
  cluster: Cluster[] = [];
  shops: Shop[] = [];
  getQueueApiCall: APICallStatus = 'NOT_STARTED';
  getLocApiCall: APICallStatus = 'NOT_STARTED';
  getClusterApiCall: APICallStatus = 'NOT_STARTED';
  getModeApiCall: APICallStatus = 'NOT_STARTED';
  initiateAssignWOApiCallStatus: APICallStatus = 'NOT_STARTED';
  modes: RepairMode[] = [];
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 10;
  sort: string | undefined;
  data: ManualApprovalHeader[] = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  myWOFormGroup: FormGroup<approvalMyWOFormModel>;
  loadGrid: boolean = false;
  masterData: ApprovalMasterData[] = [];
  regions: KeyValue[] = [];
  clusters: KeyValue[] = [];
  countries: KeyValue[] = [];
  shopCodes: KeyValue[] = [];
  workOrderRegex = /\b5\d{8}\b/g;
  private readonly workOrderNumberPattern = /^5\d{8}$/;
  equipmentRegex = /[A-Z]{4}\d{6,9}/g;
  @Input() loadedData: ManualApprovalHeader[] = [];
  @Input() loadedFormData!: FormGroup<approvalMyWOFormModel>;
  @Output() getLoadedData = new EventEmitter<ManualApprovalHeader[]>();
  @Output() getLoadedFormData = new EventEmitter<FormGroup<approvalMyWOFormModel>>();
  @Output() assignedWOData = new EventEmitter<ManualApprovalHeader[]>();
  loggedInUser = AuthHelper.user;

  get isAssignButtonEnabled(): boolean {
    if (this.disableAssignBtn) {
      return false;
    }

    const formValues = this.myWOFormGroup.getRawValue();
    const workOrderSearchLength =
      formValues.workOrderSearch && Array.isArray(formValues.workOrderSearch) ? formValues.workOrderSearch.length : 0;
    const equipmentSearchLength =
      formValues.equipmentSearch && Array.isArray(formValues.equipmentSearch) ? formValues.equipmentSearch.length : 0;

    // Check if either workOrderSearch or equipmentSearch exceeds the limit of 5
    if (workOrderSearchLength > 25 || equipmentSearchLength > 25) {
      return false;
    }

    const hasWorkOrderSearch = workOrderSearchLength > 0;
    const hasEquipmentSearch = equipmentSearchLength > 0;

    // Check if form is valid (all required fields filled) or if search fields have values
    const formIsValid = this.myWOFormGroup.valid;

    return !!(hasWorkOrderSearch || hasEquipmentSearch || formIsValid);
  }

  get isUnassignButtonEnabled(): boolean {
    return !this.isUnassigning && Array.isArray(this.data) && this.data.length > 0;
  }
  constructor(
    private fb: FormBuilder,
    private approvalWOService: ApprovalWoService,
    private toastService: ToastrService,
    private appTextService: ApplicationtTextService,
    private masterDataService: MasterDataService,
    private toastrService: ToastrService,
    private stateService: MyWoStateService,
  ) {
    this.myWOFormGroup = fb.group<approvalMyWOFormModel>({
      queue: this.fb.nonNullable.control<Queue[]>([], Validators.required),
      region: this.fb.nonNullable.control<string | null>('', Validators.required),
      countrycluster: this.fb.nonNullable.control<string | null>('', Validators.required),
      shopCountry: this.fb.nonNullable.control<string | null>(''),
      shopCode: this.fb.nonNullable.control<string | null>(''),
      mode: this.fb.nonNullable.control<RepairMode[] | null>([]),
      workOrderSearch: this.fb.nonNullable.control<string[]>([]),
      equipmentSearch: this.fb.nonNullable.control<string[]>([]),
    });
  }
  ngOnInit(): void {
    if (this.loadedFormData) {
      this.myWOFormGroup.patchValue(this.stateService.formData.value);
    }
    if (this.loadedData && this.loadedData.length > 0) {
      this.data = this.loadedData;
      this.loadGrid = true;
    }

    this.subscribeToMasterDataApiCalls();
    this.getRegionUserDetails();
    this.getAssignedWorkOrderList();
  }
  subscribeToMasterDataApiCalls() {
    this.getQueueList();
    this.getModes();
    this.region = this.locList;
    this.countrycluster = this.clusterList;
  }

  private getSelectionValue(event: Event): string {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail as unknown;

    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { value?: unknown };
      if (typeof first?.value === 'string') {
        return first.value;
      }
    }

    if (detail && typeof detail === 'object' && 'value' in detail) {
      const value = (detail as { value?: unknown }).value;
      if (typeof value === 'string') {
        return value;
      }
    }

    const target = event.target as HTMLInputElement | null;
    return target?.value || '';
  }
  getRegionUserDetails() {
    let request = {
      Loc: this.loggedInUser?.regions?.map((r) => r.code).join(','),
    };
    this.approvalWOService
      .getClusterShopReferenceDetails(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.masterData = response;
          this.initializeDropdowns();
        },
        error: (err) => {
          this.masterData = [];
          this.toastrService.showToastr(this.appTextService.unableToGetFilterDetails, 'error');
        },
      });
  }
  initializeDropdowns() {
    this.regions = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.loc == item.loc) === index)
      .map((item) => ({ code: item.loc, name: item.loc }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.clusters = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.clusterCode == item.clusterCode) === index)
      .map((item) => ({ code: item.clusterCode, name: item.clusterName, label: item.clusterCode, value: item.clusterCode }))
      .sort((a, b) => a.code.localeCompare(b.code));

    this.countries = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.shopCountry == item.shopCountry) === index)
      .map((item) => ({ code: item.shopCountry, name: item.shopCountryName || item.shopCountry, label: item.shopCountry, value: item.shopCountry }))
      .sort((a, b) => a.code.localeCompare(b.code));

    this.shopCodes = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.shopCode == item.shopCode) === index)
      .map((item) => ({ code: item.shopCode, name: item.shopCode, label: item.shopCode, value: item.shopCode }))
      .sort((a, b) => a.code.localeCompare(b.code));

    this.restoreFilteredDropdowns();
  }

  private restoreFilteredDropdowns() {
    const savedRegion = this.myWOFormGroup.controls.region.value;
    const savedCluster = this.myWOFormGroup.controls.countrycluster.value;
    const savedCountry = this.myWOFormGroup.controls.shopCountry.value;

    if (savedRegion) {
      const uniqueClusters = new Set<string>();
      this.clusters = [];
      this.masterData.forEach((row) => {
        if (!uniqueClusters.has(row.clusterCode) && row.loc == savedRegion) {
          uniqueClusters.add(row.clusterCode);
          this.clusters.push({ code: row.clusterCode, name: row.clusterCode, label: row.clusterCode, value: row.clusterCode });
        }
      });
    }

    if (savedCluster) {
      const uniqueCountries = new Set<string>();
      this.countries = [];
      this.masterData.forEach((row) => {
        if (!uniqueCountries.has(row.shopCountry) && row.clusterCode == savedCluster) {
          uniqueCountries.add(row.shopCountry);
          this.countries.push({ code: row.shopCountry, name: row.shopCountryName || row.shopCountry, label: row.shopCountry, value: row.shopCountry });
        }
      });

      const uniqueShop = new Set<string>();
      this.shopCodes = [];
      this.masterData.forEach((row) => {
        if (!uniqueShop.has(row.shopCode) && row.clusterCode == savedCluster && (!savedCountry || row.shopCountry == savedCountry)) {
          uniqueShop.add(row.shopCode);
          this.shopCodes.push({ code: row.shopCode, label: row.shopCode, value: row.shopCode });
        }
      });
    }
  }

  onRegionSelected(event: Event) {
    this.disableAssignBtn = false;
    let customEvent = event.target as HTMLInputElement;
    this.clusters = [];
    const uniqueClusters = new Set<string>();

    this.masterData.forEach((row) => {
      if (!uniqueClusters.has(row.clusterCode) && (row.loc == customEvent.value || customEvent.value == '')) {
        uniqueClusters.add(row.clusterCode);
        this.clusters.push({ code: row.clusterCode, name: row.clusterCode, label: row.clusterCode, value: row.clusterCode });
      }
    });

    const uniqueCountries = new Set<string>();
    this.countries = [];
    this.masterData.forEach((row) => {
      if ((row.loc == customEvent.value || customEvent.value == '') && !uniqueCountries.has(row.shopCountry)) {
        uniqueCountries.add(row.shopCountry);
        this.countries.push({ code: row.shopCountry, name: row.shopCountryName || row.shopCountry, label: row.shopCountry, value: row.shopCountry });
      }
    });

    this.myWOFormGroup.controls.shopCode.setValue(null);
    this.myWOFormGroup.controls.shopCountry.setValue(null);
    this.myWOFormGroup.controls.countrycluster.setValue(null);
  }
  onClusterSelected(event: Event) {
    this.disableAssignBtn = false;
    const selectedCluster = this.getSelectionValue(event);
    const uniqueCountries = new Set<string>();
    this.countries = [];
    this.masterData.forEach((row) => {
      if (!uniqueCountries.has(row.shopCountry) && (row.clusterCode == selectedCluster || selectedCluster == '')) {
        uniqueCountries.add(row.shopCountry);
        this.countries.push({ code: row.shopCountry, name: row.shopCountryName || row.shopCountry, label: row.shopCountry, value: row.shopCountry });
      }
    });

    const uniqueShop = new Set<string>();
    this.shopCodes = [];

    this.masterData.forEach((row) => {
      if (!uniqueShop.has(row.shopCode) && (row.clusterCode == selectedCluster || selectedCluster == '')) {
        uniqueShop.add(row.shopCode);
        this.shopCodes.push({ code: row.shopCode, label: row.shopCode, value: row.shopCode });
      }
    });

    this.myWOFormGroup.controls.shopCountry.setValue(null);
    this.myWOFormGroup.controls.shopCode.setValue(null);
  }

  onCountrySelected(event: Event) {
    this.disableAssignBtn = false;
    const customEvent = event.target as HTMLInputElement;
    const selectedCluster = this.myWOFormGroup.controls.countrycluster.value;
    const uniqueShop = new Set<string>();
    this.shopCodes = [];

    this.masterData.forEach((row) => {
      const clusterMatches = !selectedCluster || row.clusterCode == selectedCluster;
      const countryMatches = row.shopCountry == customEvent.value || customEvent.value == '';
      if (!uniqueShop.has(row.shopCode) && clusterMatches && countryMatches) {
        uniqueShop.add(row.shopCode);
        this.shopCodes.push({ code: row.shopCode, label: row.shopCode, value: row.shopCode });
      }
    });

    this.myWOFormGroup.controls.shopCode.setValue(null);
  }

  onClusterSearch(event: Event) {
    this.disableAssignBtn = false;
    let customEvent = event.target as HTMLInputElement;
    if (customEvent.value == '') {
      this.countries = this.masterData
        .filter((item, index, self) => self.findIndex((i) => i.shopCountry == item.shopCountry) === index)
        .map((item) => ({ code: item.shopCountry, name: item.shopCountryName || item.shopCountry, label: item.shopCountry, value: item.shopCountry }));

      this.shopCodes = this.masterData
        .filter((item, index, self) => self.findIndex((i) => i.shopCode == item.shopCode) === index)
        .map((item) => ({ code: item.shopCode, label: item.shopCode, value: item.shopCode }));

      this.myWOFormGroup.controls.shopCountry.setValue(null);
      this.myWOFormGroup.controls.shopCode.setValue(null);
    }
  }

  async getQueueList() {
    this.queueList = await firstValueFrom(
      this.approvalWOService.getAllQueueList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getQueueApiCall = 'FAILED';
          this.toastService.showToastr(this.appTextService.unableToGetQueueList, 'error');
          throw err;
        }),
      ),
    );
  }

  async getAllLoc() {
    this.locList = await firstValueFrom(
      this.approvalWOService.getAllLocList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getLocApiCall = 'FAILED';
          this.toastService.showToastr(this.appTextService.unableToGetRegionDEtails, 'error');
          throw err;
        }),
      ),
    );
    this.region = this.locList;
  }
  async getAllCluster() {
    this.clusterList = await firstValueFrom(
      this.approvalWOService.getAllClusterList().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getClusterApiCall = 'FAILED';
          this.toastService.showToastr(this.appTextService.unableToGetClusterDetails, 'error');
          throw err;
        }),
      ),
    );
    this.countrycluster = this.clusterList;
  }
  async getModes() {
    const modes = await firstValueFrom(
      this.approvalWOService.getRepairModes().pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.getModeApiCall = 'FAILED';
          this.toastService.showToastr(this.appTextService.unableToGetModeDetails, 'error');
          throw err;
        }),
      ),
    );
    this.modes = modes;
  }
  assignWO() {
    this.initiateAssignWOApiCallStatus = 'IN_PROGRESS';
    let formValues = this.myWOFormGroup.getRawValue();
    this.getLoadedFormData.emit(this.myWOFormGroup);
    this.myWOFormGroup.patchValue({
      workOrderSearch: [],
      equipmentSearch: [],
    });

    // ---- Extract queue value from Queue object or Queue[] ----
    let queueValue = '';
    if (formValues.queue) {
      // Handle both single Queue object and Queue[] array
      const queueData = Array.isArray(formValues.queue) ? formValues.queue[0] : formValues.queue;
      if (queueData && typeof queueData === 'object') {
        queueValue = queueData.queue || '';
      } else if (typeof queueData === 'string') {
        queueValue = queueData;
      }
    }

    // ---- Convert work order numbers from string[] to number[] ----
    const workOrderNumbers = (formValues.workOrderSearch || [])
      .filter((wo: string) => this.workOrderNumberPattern.test(wo))
      .map((wo: string) => Number.parseInt(wo, 10))
      .filter((num: number) => !Number.isNaN(num));

    // ---- Equipment numbers are already string[] ----
    const equipmentNumbers = formValues.equipmentSearch || [];

    // Extract modes array - convert RepairMode[] to string[]
    const modesValue = Array.isArray(formValues.mode)
      ? formValues.mode.map((m: any) => (typeof m === 'string' ? m : m.repairMode || m))
      : [];

    let reqBody: assignWOFilter = {
      queue: queueValue,
      region: formValues.region || '',
      cluster: formValues.countrycluster || '',
      country: formValues.shopCountry || undefined,
      shopCode: formValues.shopCode ? formValues.shopCode : '',
      modes: modesValue,
      actionType: 'LoadQueueButtonAction',
      workOrderNumbers: workOrderNumbers.length > 0 ? workOrderNumbers : undefined,
      equipmentNumbers: equipmentNumbers.length > 0 ? equipmentNumbers : undefined,
    };

    console.log('[FilterDropdowns] Starting assignment with reqBody:', reqBody);

    this.approvalWOService
      .assignWorkOrders(reqBody)
      .pipe(
        tap((res: any) => {
          console.log('[FilterDropdowns] Assignment successful:', res);
          this.toastService.showToastr(res.assignmentResult, 'success');
        }),
        catchError((err: unknown) => {
          console.error('[FilterDropdowns] Assignment failed, but continuing to refresh:', err);
          if (err instanceof HttpErrorResponse) {
            this.toastService.showToastr(err.error?.assignmentResult || 'Assignment failed', 'error');
          }
          // Return a success value to continue the chain
          return of({ assignmentResult: 'Failed but continuing' });
        }),
        switchMap(() => {
          console.log('[FilterDropdowns] Now calling refresh API regardless of assignment result');
          return from(this.getAssignedWorkOrderList());
        }),
        tap(() => {
          console.log('[FilterDropdowns] Refresh API completed successfully');
          this.initiateAssignWOApiCallStatus = 'COMPLETED_SUCCESFULLY';
        }),
        catchError((err: unknown) => {
          console.error('[FilterDropdowns] Error in refresh API:', err);
          this.initiateAssignWOApiCallStatus = 'FAILED';
          return of(null);
        }),
      )
      .subscribe({
        next: () => {
          console.log('[FilterDropdowns] Assignment and refresh completed');
        },
        error: (err) => {
          console.error('[FilterDropdowns] Unexpected error:', err);
          this.initiateAssignWOApiCallStatus = 'FAILED';
        },
      });
  }

  unassignWO() {
    if (!this.isUnassignButtonEnabled) {
      return;
    }

    this.isUnassigning = true;
    const req = {
      workOrderId: 0,
      actionType: 'LogOut',
      maintenanceOrderRemarks: [],
    };
    this.approvalWOService
      .unassignOnLogout(req)
      .pipe(
        switchMap(() => from(this.getAssignedWorkOrderList())),
        tap(() => {
          this.toastrService.showToastr('Work orders unassigned successfully', 'success');
          this.isUnassigning = false;
        }),
        catchError((err: unknown) => {
          const errorMessage =
            err instanceof HttpErrorResponse && err.error?.errorMessage ? err.error.errorMessage : 'Failed to unassign work orders';
          this.toastrService.showToastr(errorMessage, 'error');
          this.isUnassigning = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  async getAssignedWorkOrderList(): Promise<ManualApprovalHeader[]> {
    return new Promise((resolve, reject) => {
      this.sort = `createTimestamp:asc`;
      let reqData: searchWOList = { searchText: '' };
      console.log('[FilterDropdowns] Initial load: Fetching assigned work orders');
      this.approvalWOService
        .getAssignedWorkOrderList(reqData, this.sort)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response) => {
            const body = response.body || [];
            console.log('[FilterDropdowns] Initial load: Assigned work orders response:', body);
            this.data = body;
            this.loadGrid = true;
            this.getLoadedData.emit(this.data);
            resolve(body);
          },
          error: (err: unknown) => {
            console.error('[FilterDropdowns] Initial load: Error fetching assigned work orders:', err);
            this.data = [];
            this.loadGrid = true;
            const errorMessage =
              err instanceof HttpErrorResponse && err.error?.errorMessage ? err.error.errorMessage : 'Error loading assigned work orders';
            this.toastService.showToastr(errorMessage, 'error');
            this.getLoadedData.emit(this.data);
            reject(err);
          },
        });
    });
  }

  onQueueSelect(event: Event) {
    this.disableAssignBtn = false;
  }

  onShopSelected(event: Event) {
    this.disableAssignBtn = false;
  }
  onModeSelected(event: Event) {
    this.disableAssignBtn = false;
  }
}
